import * as React from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useSearchParams,
  type Location,
} from "react-router-dom";
import { useNavigation, updatePageLayoutHeight } from "../../utils";
import { RouteShell } from "../../navigation";
import {
  KeepAliveHomePage,
  createLazyRouteWithPageWrapper,
  createLazyRoute,
  KeepAliveEventCenterPage,
  KeepAliveAgentCenterPage,
  KeepAliveInviteFriendsPage,
  KeepAliveVIPPage,
  KeepAliveProfilePage,
} from "../LazyPage";
import { withMtgClickId } from "@/utils/mtgClickId";
import { ROUTES } from "../../types/routes";
import { ProtectedRoute } from "./ProtectedRoute";
import { navigationPolicy } from "@/navigation/policy";
// import { GuestRoute } from "./GuestRoute";
import { useUserStore } from "../../modules/user";
import { useBottomNavInviteTarget } from "../../modules/homeDisplay/hooks";
import { Suspense, useEffect } from "react";
import PageLoading from "../PageLoading";
import { openLoginRegisterPopup } from "../LoginRegisterPopup";
import { openDepositPopup } from "../DepositPopup/triggerDepositPopup";
import type { CashbackBoostRechargeState } from "../CashbackBoost/types";

// 懒加载的页面组件（带PageWrapper）
const LazyMaintain = createLazyRouteWithPageWrapper(
  () => import("../../pages/Maintain")
);
// 三方登录（Google / Facebook）新用户补充资料页：ThirdPartyAuth 检测到未注册时跳这里
const LazySetAccount = createLazyRouteWithPageWrapper(
  () => import("../../pages/SetAccount")
);
const LazyAgentCommissionNew = createLazyRouteWithPageWrapper(
  () => import("../../pages/AgentCommissionNew")
);
const LazyAgentPanel = createLazyRouteWithPageWrapper(
  () => import("../../pages/AgentPanel")
);
const LazyWithdraw = createLazyRouteWithPageWrapper(
  () => import("../../pages/Withdraw")
);

const LazyRecordsDeposit = createLazyRouteWithPageWrapper(
  () => import("../../pages/RecordsDeposit")
);
const LazyAccountDetails = createLazyRouteWithPageWrapper(
  () => import("../../pages/AccountDetails")
);
const LazyRedeem = createLazyRouteWithPageWrapper(
  () => import("../../pages/Redeem")
);
const LazyPromotionDetails = createLazyRouteWithPageWrapper(
  () => import("../../pages/PromotionDetails")
);
const LazyRechargePromo = createLazyRouteWithPageWrapper(
  () => import("../../pages/RechargePromo")
);
const LazyEventDetails = createLazyRouteWithPageWrapper(
  () => import("../../pages/EventDetails")
);
const LazyRescueFundRule = createLazyRouteWithPageWrapper(
  () => import("../../pages/RescueFund/RescueFundRule")
);
// 实时反水「反水比例」档位页（活动中心实时反水卡片点进）
// 红包雨（新）活动页：页面自带 PageWrapper（标题 = 活动名），所以不用 WithPageWrapper
const LazyRedEnvelopeRainPage = createLazyRoute(
  () => import("../../pages/RedEnvelopeRain")
);
const LazyRealtimeRebateTiers = createLazyRouteWithPageWrapper(
  () => import("../../pages/EventCenter/RealtimeRebate/RealtimeRebateTiers")
);
const LazyMessageCenter = createLazyRouteWithPageWrapper(
  () => import("../../pages/MessageCenter")
);
const LazyMessage = createLazyRoute(
  () => import("../../pages/MessageCenter/part/Message")
);
const LazyGameEmbed = createLazyRouteWithPageWrapper(
  () => import("../../pages/GameEmbed")
);
const LazyGameDetail = createLazyRouteWithPageWrapper(
  () => import("../../pages/GameDetail")
);
const LazyBetbyGame = createLazyRoute(() => import("../../pages/BetbyGame"));
const LazySportsGame = createLazyRoute(() => import("../../pages/SportsGame"));
const LazyGameSearch = createLazyRouteWithPageWrapper(
  () => import("../../pages/GameSearch")
);
// 游戏分类页：由 gameSearch 轴的 variant 提供（template2 = supergol 分类页；template1 回首页），页面自带顶栏
// 安全中心整页（自带 SgPageHeader 顶栏；仅 supergol 个人中心跳转）
const LazySecurityCenter = createLazyRoute(
  () => import("../../pages/SecurityCenter")
);
const LazySecurityVerifyPassword = createLazyRoute(() =>
  import("../../pages/SecurityCenter").then(m => ({
    default: m.VerifyPasswordPage,
  }))
);
const LazySecurityChangePassword = createLazyRoute(() =>
  import("../../pages/SecurityCenter").then(m => ({
    default: m.ChangePasswordPage,
  }))
);
const LazySecurityBindEmail = createLazyRoute(() =>
  import("../../pages/SecurityCenter").then(m => ({
    default: m.BindEmailPage,
  }))
);
const LazyGameCategory = createLazyRoute(() =>
  import("../../pages/GameSearch").then(m => ({ default: m.GameCategoryPage }))
);
const LazyLandingPage = createLazyRouteWithPageWrapper(
  () => import("../../pages/LandingPage")
);
const LazyComponentShowcase = createLazyRouteWithPageWrapper(
  () => import("../../pages/ComponentShowcase")
);
const LazyRedpacket = createLazyRouteWithPageWrapper(
  () => import("../../pages/Redpacket")
);
const LazyUnpaidOrders = createLazyRouteWithPageWrapper(
  () => import("../../pages/UnpaidOrders")
);
const LazyPayCvu = createLazyRoute(() => import("../../pages/PayCvu"));
const LazyWinPayCvu = createLazyRoute(() => import("../../pages/WinPayCvu"));

/**
 * 访问 /login 或 /register 时重定向到首页并弹出登录注册底部弹窗
 */
const LoginPopupRedirect: React.FC<{
  defaultTab?: "sms" | "password";
  defaultMode?: "login" | "register";
}> = ({ defaultTab, defaultMode }) => {
  useEffect(() => {
    openLoginRegisterPopup({ defaultTab, defaultMode });
  }, [defaultTab, defaultMode]);
  return <Navigate to={withMtgClickId(ROUTES.HOME)} replace />;
};

const IS_SG_PRESET = import.meta.env.VITE_PROFILE_VARIANT === "template7";
const LazySgRechargePage = createLazyRouteWithPageWrapper(
  () => import("../../pages/Recharge/sg/SgRechargePage")
);

const DepositPopupRedirect: React.FC = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const activityId = searchParams.get("activityId") || undefined;
  const cashbackBoost = (
    location.state as { cashbackBoost?: CashbackBoostRechargeState }
  )?.cashbackBoost;
  useEffect(() => {
    openDepositPopup({
      ...(activityId ? { activityId } : {}),
      ...(cashbackBoost?.recordId ? { cashbackBoost } : {}),
    });
  }, [activityId, cashbackBoost]);
  return <Navigate to={withMtgClickId(ROUTES.HOME)} replace />;
};

// 路由配置
const AppRouter: React.FC = () => {
  const { navigate } = useNavigation();
  const location = useLocation();
  const inviteTarget = useBottomNavInviteTarget();
  // 监听路由变化，动态设置页面导航栏高度（通过CSS变量）
  React.useEffect(() => {
    updatePageLayoutHeight(location);
  }, [location.pathname, location.state]);

  // 监听全局路由跳转事件
  React.useEffect(() => {
    const handleNavigateToLogin = (event: CustomEvent) => {
      if (
        useUserStore.getState().isAuthenticated() &&
        event.detail.reason === "session_expired"
      ) {
        useUserStore.getState().logout();
      }
    };

    // 添加事件监听器
    window.addEventListener(
      "navigateToLogin",
      handleNavigateToLogin as EventListener
    );

    // 清理事件监听器
    return () => {
      window.removeEventListener(
        "navigateToLogin",
        handleNavigateToLogin as EventListener
      );
    };
  }, [navigate]);

  // 路由表：壳（Layout / 下拉刷新 / 转场）由 navigation 轴的 RouteShell 决定，这里只管表本身。
  // template2 的转场壳会传入「正在展示的 location」让旧层在退场期间继续渲染旧路由。
  const renderRoutes = (displayLocation?: Location) => (
    <Routes location={displayLocation}>
      {/* 公开路由 - 不需要登录 */}

      {/* 首页路由 */}
      <Route path={ROUTES.HOME} element={<KeepAliveHomePage />} />
      <Route path={ROUTES.HOME_ALT} element={<KeepAliveHomePage />} />

      {/* 用户认证路由 — 重定向到首页并弹出底部登录注册弹窗 */}
      <Route
        path={ROUTES.LOGIN}
        element={
          <LoginPopupRedirect defaultMode="login" defaultTab="password" />
        }
      />
      <Route
        path={ROUTES.REGISTER}
        element={<LoginPopupRedirect defaultTab="sms" />}
      />
      {/* 三方登录新用户补资料：非弹窗页面，独立路由 */}
      <Route path={ROUTES.SET_ACCOUNT} element={<LazySetAccount />} />

      {/* 维护页面路由 */}
      <Route path={ROUTES.REPAIR} element={<LazyMaintain />} />
      <Route path={ROUTES.MAINTAIN} element={<LazyMaintain />} />

      {/* 落地页路由 */}
      <Route path={ROUTES.LANDING_PAGE} element={<LazyLandingPage />} />

      {/* 红包活动路由 */}
      <Route path={ROUTES.REDPACKET} element={<LazyRedpacket />} />

      {/* 受保护的路由 - 需要登录 */}

      {/* VIP页面路由 */}
      <Route
        path={ROUTES.VIP}
        element={
          <ProtectedRoute>
            <KeepAliveVIPPage />
          </ProtectedRoute>
        }
      />
      {/* 用户相关路由 — Profile 页面缺省支持未登录访问（内部分歧展示）；navigation 轴可把它列入 loginRequiredRoutes（supergol） */}
      <Route
        path={ROUTES.PROFILE}
        element={
          navigationPolicy.loginRequiredRoutes?.includes(ROUTES.PROFILE) ? (
            <ProtectedRoute>
              <KeepAliveProfilePage />
            </ProtectedRoute>
          ) : (
            <KeepAliveProfilePage />
          )
        }
      />
      {inviteTarget !== "INVITE_PAGE" && (
        <Route
          path={ROUTES.AGENT_CENTER}
          element={<KeepAliveAgentCenterPage />}
        />
      )}
      {inviteTarget === "INVITE_PAGE" && (
        <Route
          path={ROUTES.INVITE_FRIENDS}
          element={<KeepAliveInviteFriendsPage />}
        />
      )}
      <Route
        path={ROUTES.AGENT_COMMISSION}
        element={
          <ProtectedRoute>
            <LazyAgentCommissionNew />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.AGENT_PANEL}
        element={
          <ProtectedRoute>
            <LazyAgentPanel />
          </ProtectedRoute>
        }
      />
      {/* supergol（profile template7）：充值是独立路由页（竞品 /recharge/apply）；其它皮肤仍是「拉起弹层 + 回首页」 */}
      <Route
        path={ROUTES.RECHARGE}
        element={
          IS_SG_PRESET ? (
            <ProtectedRoute>
              <LazySgRechargePage />
            </ProtectedRoute>
          ) : (
            <DepositPopupRedirect />
          )
        }
      />
      <Route
        path={ROUTES.UNPAID_ORDERS}
        element={
          <ProtectedRoute>
            <LazyUnpaidOrders />
          </ProtectedRoute>
        }
      />
      <Route path={ROUTES.PAY_CVU} element={<LazyPayCvu />} />
      <Route path={ROUTES.WIN_PAY_CVU} element={<LazyWinPayCvu />} />
      <Route
        path={ROUTES.RECHARGE_PROMO}
        element={
          <ProtectedRoute>
            <LazyRechargePromo />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.EVENT_DETAILS}
        element={
          <ProtectedRoute>
            <LazyEventDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.WITHDRAW}
        element={
          <ProtectedRoute>
            <LazyWithdraw />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.RECORDS_DEPOSIT}
        element={
          <ProtectedRoute>
            <LazyRecordsDeposit />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.PAY_RECORDS}
        element={
          <ProtectedRoute>
            <LazyRecordsDeposit />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.AUDIT_DETAIL}
        element={
          <ProtectedRoute>
            <LazyRecordsDeposit />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.ACCOUNT_DETAILS}
        element={
          <ProtectedRoute>
            <LazyAccountDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.REDEEM}
        element={
          <ProtectedRoute>
            <LazyRedeem />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.PROMOTION_DETAILS}
        element={<LazyPromotionDetails />}
      />
      <Route
        path={ROUTES.RESCUE_FUND_RULE}
        element={
          <ProtectedRoute>
            <LazyRescueFundRule />
          </ProtectedRoute>
        }
      />

      <Route path={ROUTES.MESSAGE_CENTER} element={<LazyMessageCenter />} />
      <Route
        path={ROUTES.SECURITY_CENTER}
        element={
          <ProtectedRoute>
            <LazySecurityCenter />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.SECURITY_VERIFY_PASSWORD}
        element={
          <ProtectedRoute>
            <LazySecurityVerifyPassword />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.SECURITY_CHANGE_PASSWORD}
        element={
          <ProtectedRoute>
            <LazySecurityChangePassword />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.SECURITY_BIND_EMAIL}
        element={
          <ProtectedRoute>
            <LazySecurityBindEmail />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.MESSAGE}
        element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoading />}>
              <LazyMessage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.EVENT_CENTER}
        element={<KeepAliveEventCenterPage />}
      />
      <Route
        path={ROUTES.REALTIME_REBATE_TIERS}
        element={
          <ProtectedRoute>
            <LazyRealtimeRebateTiers />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.RED_ENVELOPE_RAIN}
        element={
          <ProtectedRoute>
            <LazyRedEnvelopeRainPage />
          </ProtectedRoute>
        }
      />

      {/* 游戏相关路由 */}
      <Route path={ROUTES.GAME_SEARCH} element={<LazyGameSearch />} />
      <Route path={ROUTES.GAME_CATEGORY} element={<LazyGameCategory />} />
      <Route
        path={ROUTES.EMBED}
        element={
          <ProtectedRoute>
            <LazyGameEmbed />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.GAME_DETAIL}
        element={
          <ProtectedRoute>
            <LazyGameDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.BETBY}
        element={
          <ProtectedRoute>
            <LazyBetbyGame />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.SPORTS_GAME}
        element={
          <ProtectedRoute>
            <LazySportsGame />
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.GAMES}
        element={<Navigate to={ROUTES.HOME} replace />}
      />
      <Route
        path={`${ROUTES.GAMES}/*`}
        element={<Navigate to={ROUTES.HOME} replace />}
      />

      <Route
        path={ROUTES.SETTINGS}
        element={<Navigate to={ROUTES.HOME} replace />}
      />

      {/* 组件展示页（开发调试） */}
      <Route path={ROUTES.COMPONENT_TEST} element={<LazyComponentShowcase />} />

      {/* 默认重定向到首页 */}
      <Route
        path={ROUTES.NOT_FOUND}
        element={<Navigate to={withMtgClickId(ROUTES.HOME)} replace />}
      />
    </Routes>
  );
  return <RouteShell renderRoutes={renderRoutes} />;
};

export default AppRouter;
